import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Collapse from '@mui/material/Collapse';
import AddIcon from '@mui/icons-material/Add';
import {
  BEST_OF_CHOICES,
  DEFAULT_BRACKET_BEST_OF,
  DEFAULT_COMPANY_ID,
  DEFAULT_GROUP_BEST_OF,
  DEFAULT_TOURNAMENT_SPORT,
  SPORT_IDS,
  TABLE_MAX,
  TABLE_MIN,
  type SportId,
} from '../domain';
import { useTournament } from '../state/TournamentContext';
import { useTranslation } from '../state/I18nContext';
import { uploadCover } from '../data/apiClient';
import { CoverImagePicker } from './CoverImagePicker';

interface ConfigScreenProps {
  onCreated: () => void;
  onCancel: () => void;
}

export function ConfigScreen({ onCreated, onCancel }: ConfigScreenProps) {
  const { create, companies } = useTournament();
  const { t } = useTranslation();

  const [name, setName] = useState('');
  // The sport decides the scoring rules (table-tennis best-of series vs FIFA
  // football) and who competes (players vs teams). Fixed once created.
  const [sportId, setSportId] = useState<SportId>(DEFAULT_TOURNAMENT_SPORT);
  const isFootball = sportId === 'FOOTBALL';
  const [companyId, setCompanyId] = useState(DEFAULT_COMPANY_ID);
  // The companies list is scoped to what the signed-in user may act on, and
  // may not include the default company. Snap the selection onto the first
  // accessible company whenever it points outside the list (initial load, or
  // a permissions change), so the create request is always one the server
  // will accept.
  useEffect(() => {
    if (companies.length > 0 && !companies.some((c) => c.id === companyId)) {
      setCompanyId(companies[0].id);
    }
  }, [companies, companyId]);
  const [description, setDescription] = useState('');
  const [coverBlob, setCoverBlob] = useState<Blob | null>(null);
  // Local-datetime string from the picker (e.g. "2026-07-01T18:30"), or '' when unset.
  const [startDate, setStartDate] = useState('');
  const [acceptRegistration, setAcceptRegistration] = useState(false);
  // Sign-up deadline as a local-datetime string, or '' when unset.
  const [registrationCloses, setRegistrationCloses] = useState('');
  const [tables, setTables] = useState(4);
  const [groupBestOf, setGroupBestOf] = useState<number>(DEFAULT_GROUP_BEST_OF);
  const [bracketBestOf, setBracketBestOf] = useState<number>(
    DEFAULT_BRACKET_BEST_OF,
  );
  // The Final and third-place playoff can run a different (usually longer)
  // series than the rest of the bracket; defaults to the bracket length.
  const [finalBestOf, setFinalBestOf] = useState<number>(DEFAULT_BRACKET_BEST_OF);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const nameValid = name.trim().length > 0;
  const tablesValid =
    Number.isInteger(tables) && tables >= TABLE_MIN && tables <= TABLE_MAX;
  // When public registration is on, a closing date is required. Football never
  // accepts public sign-ups (they register individuals, not teams).
  const registrationValid =
    isFootball || !acceptRegistration || registrationCloses.length > 0;
  const valid = nameValid && tablesValid && registrationValid;

  // A local-datetime picker value has no timezone; `new Date(value)` parses it in
  // the browser's local zone, which is what the organiser intends.
  const startsAt = startDate ? new Date(startDate).getTime() : null;
  const registrationClosesAt =
    acceptRegistration && registrationCloses
      ? new Date(registrationCloses).getTime()
      : null;

  const submit = async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      // Upload the cover first (if any) so the create request can persist its
      // public URL. A failed upload aborts creation and keeps the form intact.
      const coverUrl = coverBlob ? await uploadCover(coverBlob) : null;
      await create({
        name: name.trim(),
        companyId,
        sportId,
        description: description.trim(),
        coverUrl,
        startsAt: Number.isNaN(startsAt) ? null : startsAt,
        acceptPublicRegistration: isFootball ? false : acceptRegistration,
        registrationClosesAt:
          isFootball ||
          (registrationClosesAt !== null && Number.isNaN(registrationClosesAt))
            ? null
            : registrationClosesAt,
        config: {
          totalPlayers: 0,
          availableTables: tables,
          // A football match is a single game; the server enforces this too.
          groupBestOf: isFootball ? 1 : groupBestOf,
          bracketBestOf: isFootball ? 1 : bracketBestOf,
          finalBestOf: isFootball ? 1 : finalBestOf,
        },
      });
      onCreated();
    } catch (e: unknown) {
      setSubmitError(
        e instanceof Error ? e.message : t('admin.config.createError'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Card sx={{ width: '100%', maxWidth: 560, border: 1, borderColor: 'divider' }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            {t('admin.config.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('admin.config.intro')}
          </Typography>

          <Stack spacing={2}>
            <TextField
              label={t('admin.config.name.label')}
              fullWidth
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={!nameValid && name.length > 0}
              helperText={
                !nameValid && name.length > 0
                  ? t('admin.config.name.required')
                  : ' '
              }
            />
            <TextField
              select
              fullWidth
              label={t('admin.config.sport.label')}
              value={sportId}
              onChange={(e) => setSportId(e.target.value)}
              helperText={
                isFootball
                  ? t('admin.config.sport.footballHelper')
                  : t('admin.config.sport.helper')
              }
            >
              {SPORT_IDS.map((id) => (
                <MenuItem key={id} value={id}>
                  {t(`sports.${id}`)}
                </MenuItem>
              ))}
            </TextField>
            {companies.length > 1 && (
              <TextField
                select
                fullWidth
                label={t('admin.config.company.label')}
                value={companyId}
                onChange={(e) => setCompanyId(Number(e.target.value))}
                helperText={t('admin.config.company.helper')}
              >
                {companies.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              label={t('admin.config.description.label')}
              fullWidth
              multiline
              minRows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              helperText={t('admin.config.description.helper')}
            />
            <CoverImagePicker
              blob={coverBlob}
              onChange={setCoverBlob}
              disabled={submitting}
            />
            <TextField
              label={t('admin.config.startDate.label')}
              type="datetime-local"
              fullWidth
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              helperText={t('admin.config.startDate.helper')}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            {!isFootball && (
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={acceptRegistration}
                      onChange={(e) => setAcceptRegistration(e.target.checked)}
                    />
                  }
                  label={t('admin.config.registration.toggleLabel')}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 0.5 }}>
                  {t('admin.config.registration.toggleHelper')}
                </Typography>
                <Collapse in={acceptRegistration}>
                  <TextField
                    label={t('admin.config.registration.closesLabel')}
                    type="datetime-local"
                    fullWidth
                    value={registrationCloses}
                    onChange={(e) => setRegistrationCloses(e.target.value)}
                    error={acceptRegistration && !registrationValid}
                    helperText={
                      acceptRegistration && !registrationValid
                        ? t('admin.config.registration.closesRequired')
                        : t('admin.config.registration.closesHelper')
                    }
                    slotProps={{ inputLabel: { shrink: true } }}
                    sx={{ mt: 1.5 }}
                  />
                </Collapse>
              </Box>
            )}
            <TextField
              label={
                isFootball
                  ? t('admin.config.pitches.label')
                  : t('admin.config.tables.label')
              }
              type="number"
              fullWidth
              value={Number.isNaN(tables) ? '' : tables}
              onChange={(e) => setTables(Math.trunc(Number(e.target.value)))}
              error={!tablesValid}
              helperText={t('admin.config.tables.helper', {
                min: TABLE_MIN,
                max: TABLE_MAX,
              })}
              slotProps={{ htmlInput: { min: TABLE_MIN, max: TABLE_MAX } }}
            />
            {!isFootball && (
              <>
                <TextField
                  select
                  fullWidth
                  label={t('admin.config.bestOf.groupLabel')}
                  value={groupBestOf}
                  onChange={(e) => setGroupBestOf(Number(e.target.value))}
                >
                  {BEST_OF_CHOICES.map((n) => (
                    <MenuItem key={n} value={n}>
                      {t('admin.config.bestOf.option', { n })}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  fullWidth
                  label={t('admin.config.bestOf.bracketLabel')}
                  value={bracketBestOf}
                  onChange={(e) => setBracketBestOf(Number(e.target.value))}
                  helperText={t('admin.config.bestOf.bracketHelper')}
                >
                  {BEST_OF_CHOICES.map((n) => (
                    <MenuItem key={n} value={n}>
                      {t('admin.config.bestOf.option', { n })}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  fullWidth
                  label={t('admin.config.bestOf.finalLabel')}
                  value={finalBestOf}
                  onChange={(e) => setFinalBestOf(Number(e.target.value))}
                  helperText={t('admin.config.bestOf.finalHelper')}
                >
                  {BEST_OF_CHOICES.map((n) => (
                    <MenuItem key={n} value={n}>
                      {t('admin.config.bestOf.option', { n })}
                    </MenuItem>
                  ))}
                </TextField>
              </>
            )}
          </Stack>

          {submitError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {submitError}
            </Alert>
          )}

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button onClick={onCancel} disabled={submitting}>
              {t('admin.common.cancel')}
            </Button>
            <Button
              size="large"
              variant="contained"
              startIcon={
                submitting ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <AddIcon />
                )
              }
              disabled={!valid || submitting}
              onClick={() => void submit()}
            >
              {submitting
                ? t('admin.config.creating')
                : t('admin.config.create')}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
